import type { Bytes } from './binary.ts';
import type {
  ContentHash,
  ExamId,
  IsoDateTime,
  ItemId,
  LanguageTag,
  MediaId,
  OptionId,
  QuestionId,
  TargetId,
  TaxonomyId,
} from './brands.ts';
import type { QuestionType } from '../schema/question-types.ts';

/**
 * The Canonical Internal Exam Model.
 *
 * Everything downstream of the importer — the engine, scoring, statistics, the UI — depends on
 * this and never on the file format. That is the whole architectural bet of the project: when
 * schema 2.0 arrives, the migration and the normalizer change, and nothing else does.
 *
 * Three properties this model guarantees and the file format does not:
 *
 *  - **Complete.** Every default has been applied. `scoring.points`, `selection`, `normalization`
 *    and the rest are always present, so no consumer has to remember what the default was.
 *  - **Serialisable.** Plain data only — no classes, no functions, no `Map` or `Set`. It crosses
 *    the Worker boundary by structured clone and appears verbatim in test snapshots.
 *  - **Frozen.** Deeply frozen in development. Mutating it is a bug, not a shortcut.
 */

export type Difficulty = 'easy' | 'medium' | 'hard';
export type CognitiveLevel = 'recall' | 'comprehension' | 'application' | 'analysis';

export type MediaRole =
  | 'essential-content'
  | 'illustration'
  | 'diagram'
  | 'screenshot'
  | 'chart'
  | 'table-image'
  | 'decorative';

export type AllowedImageMime = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

/**
 * Where the image actually is, after normalisation.
 *
 * The file's declared `availability` is a claim; this is the resolved fact. A `package` reference
 * with no package loaded, or a `remote` URL that is never fetched, both become `missing` here — so
 * the UI has one thing to check rather than a rule to re-derive.
 */
export type MediaAvailability = 'missing' | 'embedded';

export interface MediaPayload { readonly kind: 'embedded'; readonly bytes: Bytes }

export interface MediaRef {
  readonly id: MediaId;
  readonly role: MediaRole;
  readonly availability: MediaAvailability;
  readonly alt?: string;
  readonly description?: string;
  readonly originalFilename?: string;
  readonly mimeType?: AllowedImageMime;
  readonly payload?: MediaPayload;
  readonly source?: string;
  /** Set when the file declared `package` or `remote`, so the UI can say why it is not shown. */
  readonly unresolvedFrom?: 'package' | 'remote';
}

export interface Option {
  readonly id: OptionId;
  readonly text: string;
  readonly media: readonly MediaRef[];
  readonly feedback?: string;
  readonly fixedPosition: boolean;
}

export interface Classification {
  readonly categoryId?: TaxonomyId;
  readonly topicIds: readonly TaxonomyId[];
  readonly tags: readonly string[];
  readonly difficulty?: Difficulty;
  readonly cognitiveLevel?: CognitiveLevel;
}

export interface QuestionContent {
  readonly prompt: string;
  readonly instructions?: string;
  /** Always resolved: falls back to the exam language, so the UI never has to. */
  readonly language: LanguageTag;
  readonly media: readonly MediaRef[];
}

export interface QuestionScoring {
  readonly points: number;
  readonly mode: 'all-or-nothing' | 'partial';
}

export interface Explanation {
  readonly summary?: string;
  readonly detail?: string;
  readonly media: readonly MediaRef[];
}

export interface Reference {
  readonly title: string;
  readonly url?: string;
  readonly publisher?: string;
  readonly locator?: string;
  readonly retrievedAt?: IsoDateTime;
}

export interface QuestionRevision {
  readonly at: IsoDateTime;
  readonly by?: string;
  readonly reason: string;
  readonly changedFields: readonly string[];
  readonly note?: string;
}

export interface QuestionMetadata {
  readonly status: 'draft' | 'reviewed' | 'deprecated';
  readonly source?: { readonly externalId?: string; readonly title?: string; readonly locator?: string };
  readonly createdAt?: IsoDateTime;
  readonly updatedAt?: IsoDateTime;
}

interface QuestionBase {
  readonly id: QuestionId;
  readonly classification: Classification;
  readonly content: QuestionContent;
  readonly scoring: QuestionScoring;
  readonly explanation?: Explanation;
  readonly references: readonly Reference[];
  readonly revisions: readonly QuestionRevision[];
  readonly metadata: QuestionMetadata;
  readonly extensions: Readonly<Record<string, unknown>>;
}

export interface SingleChoiceQuestion extends QuestionBase {
  readonly type: 'single-choice';
  readonly interaction: {
    readonly options: readonly Option[];
    readonly correctOptionId: OptionId;
    readonly shuffleOptions?: boolean;
  };
}

export interface MultipleChoiceQuestion extends QuestionBase {
  readonly type: 'multiple-choice';
  readonly interaction: {
    readonly options: readonly Option[];
    readonly correctOptionIds: readonly OptionId[];
    /** Derived from the answer key when the file does not say. */
    readonly selection: { readonly min: number; readonly max: number };
    readonly shuffleOptions?: boolean;
  };
}

export interface MatchingQuestion extends QuestionBase {
  readonly type: 'matching';
  readonly interaction: {
    readonly items: readonly Option[];
    readonly targets: readonly Option[];
    readonly pairs: readonly { readonly itemId: ItemId; readonly targetId: TargetId }[];
    readonly allowTargetReuse: boolean;
    readonly shuffleTargets?: boolean;
  };
}

export interface OrderingQuestion extends QuestionBase {
  readonly type: 'ordering';
  readonly interaction: {
    readonly items: readonly Option[];
    readonly correctOrder: readonly ItemId[];
    readonly orderLabel?: { readonly first?: string; readonly last?: string };
  };
}

export interface TextNormalization {
  readonly ignoreCase: boolean;
  readonly trimWhitespace: boolean;
  readonly collapseWhitespace: boolean;
  readonly ignoreAccents: boolean;
  readonly ignorePunctuation: boolean;
}

export interface TextQuestion extends QuestionBase {
  readonly type: 'text';
  readonly interaction: {
    readonly evaluation: 'auto' | 'self';
    readonly inputMode: 'single-line' | 'multi-line';
    readonly maxLength: number;
    readonly accepted: readonly string[];
    readonly normalization: TextNormalization;
    readonly sampleAnswer?: string;
  };
}

export type CanonicalQuestion =
  | SingleChoiceQuestion
  | MultipleChoiceQuestion
  | MatchingQuestion
  | OrderingQuestion
  | TextQuestion;

/** Maps a type to its question, so registries can be typed without `any`. */
export type QuestionByType = { [Q in CanonicalQuestion as Q['type']]: Q };

export interface TaxonomyNode {
  readonly id: TaxonomyId;
  readonly label: string;
  readonly description?: string;
}

export interface Taxonomy {
  /** False when the ids were collected from the questions instead of declared by the author. */
  readonly declared: boolean;
  readonly categories: readonly TaxonomyNode[];
  readonly topics: readonly TaxonomyNode[];
}

export interface ExamDefaults {
  readonly pointsPerQuestion: number;
  readonly timeLimitMinutes?: number;
  readonly passingScorePercent?: number;
}

export interface ExamMeta {
  readonly id: ExamId;
  readonly title: string;
  readonly description?: string;
  readonly contentVersion: string;
  readonly language: LanguageTag;
  readonly authors: readonly { readonly name: string; readonly url?: string }[];
  readonly license?: string;
  readonly source?: {
    readonly title?: string;
    readonly publisher?: string;
    readonly url?: string;
    readonly retrievedAt?: IsoDateTime;
  };
  readonly derivedFrom?: {
    readonly examId: ExamId;
    readonly contentVersion: string;
    readonly contentHash: ContentHash;
    readonly derivedAt: IsoDateTime;
    readonly producer?: string;
  };
  readonly defaults: ExamDefaults;
  readonly createdAt?: IsoDateTime;
  readonly updatedAt?: IsoDateTime;
  readonly extensions: Readonly<Record<string, unknown>>;
}

/**
 * Derived lookups, built once so neither the engine nor the UI ever scans the question list.
 *
 * `Record` rather than `Map` on purpose: this object crosses the Worker boundary and shows up in
 * snapshots, and `Map` survives structured clone but not `JSON.stringify`.
 */
export interface ExamIndex {
  readonly byId: Readonly<Record<string, number>>;
  readonly byCategory: Readonly<Record<string, readonly QuestionId[]>>;
  readonly byTopic: Readonly<Record<string, readonly QuestionId[]>>;
  readonly byTag: Readonly<Record<string, readonly QuestionId[]>>;
  readonly byDifficulty: Readonly<Record<string, readonly QuestionId[]>>;
  readonly byType: Readonly<Record<QuestionType, readonly QuestionId[]>>;
}

export interface CanonicalExam {
  readonly contentHash: ContentHash;
  /** The version this build works with, after migration. */
  readonly schemaVersion: string;
  /** The version the file declared, before migration. */
  readonly sourceSchemaVersion: string;
  readonly meta: ExamMeta;
  readonly taxonomy: Taxonomy;
  readonly questions: readonly CanonicalQuestion[];
  readonly index: ExamIndex;
}

export function questionAt(exam: CanonicalExam, id: QuestionId): CanonicalQuestion | undefined {
  const position = exam.index.byId[id];
  return position === undefined ? undefined : exam.questions[position];
}

/** Every option-like item of a question, whatever the type calls them. */
export function optionsOf(question: CanonicalQuestion): readonly Option[] {
  switch (question.type) {
    case 'single-choice':
    case 'multiple-choice':
      return question.interaction.options;
    case 'matching':
      return [...question.interaction.items, ...question.interaction.targets];
    case 'ordering':
      return question.interaction.items;
    case 'text':
      return [];
  }
}
